import { SpaceDocument, SpaceDocumentMigration, SpaceMigrationError } from 'types/space';

/**
 * Current catalog generation new documents are authored/validated against. Bumping this without
 * registering a matching `SpaceDocumentMigration` leaves older stored documents unreadable — do
 * both in the same reviewed change (SC7).
 */
export const CURRENT_CATALOG_VERSION = 1;

/**
 * Applies the read-time migration chain to bring `doc` from its stamped `catalogVersion` up to
 * `targetCatalogVersion` (default: `CURRENT_CATALOG_VERSION`), one registered step at a time.
 * Fail-closed: a document newer than the target, or a chain with a missing step, is an error —
 * never a best-effort pass-through (SC7).
 */
export function migrateSpaceDocument(
  doc: SpaceDocument,
  migrations: SpaceDocumentMigration[] = [],
  targetCatalogVersion: number = CURRENT_CATALOG_VERSION
): SpaceDocument {
  // Guard FIRST. `undefined === target`, `undefined > target` and `undefined < target`
  // are all false, so an unstamped document would fall straight through the chain
  // unmigrated and unflagged — permanently exempt from every future catalog migration,
  // the exact opposite of this function's fail-closed contract.
  if (!Number.isInteger(doc.catalogVersion)) {
    throw newMigrationError(
      'MISSING_CATALOG_VERSION',
      `Document catalogVersion must be an integer, received ${JSON.stringify(doc.catalogVersion)}`,
      doc.catalogVersion as number,
      targetCatalogVersion
    );
  }

  if (doc.catalogVersion === targetCatalogVersion) {
    return doc;
  }

  if (doc.catalogVersion > targetCatalogVersion) {
    throw newMigrationError(
      'DOCUMENT_NEWER_THAN_TARGET',
      `Document catalogVersion ${doc.catalogVersion} is newer than target catalogVersion ${targetCatalogVersion}`,
      doc.catalogVersion,
      targetCatalogVersion
    );
  }

  const migrationsByFromVersion = new Map(migrations.map((migration) => [migration.fromCatalogVersion, migration]));

  let current = doc;
  while (current.catalogVersion < targetCatalogVersion) {
    const migration = migrationsByFromVersion.get(current.catalogVersion);
    if (!migration) {
      throw newMigrationError(
        'MIGRATION_PATH_NOT_FOUND',
        `No migration registered from catalogVersion ${current.catalogVersion} toward target ${targetCatalogVersion}`,
        doc.catalogVersion,
        targetCatalogVersion
      );
    }

    const migrated = migration.migrate(current);
    current = { ...migrated, catalogVersion: migration.fromCatalogVersion + 1 };
  }

  return current;
}

function newMigrationError(
  code: SpaceMigrationError['code'],
  message: string,
  fromCatalogVersion: number,
  targetCatalogVersion: number
): SpaceMigrationError {
  const error = new Error(message) as SpaceMigrationError;
  error.code = code;
  error.fromCatalogVersion = fromCatalogVersion;
  error.targetCatalogVersion = targetCatalogVersion;
  return error;
}
